import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  Pulse,
  HourglassMedium,
  ArrowsClockwise,
  Warning,
  Lightning,
  ArrowRight,
  Play,
  Stop,
  ArrowClockwise,
  Stamp,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react';

/* ── Status config ── */
const STATUS_CONFIG = {
  queued: { label: 'Queued', color: '#7D8896', bg: 'rgba(107,118,133,0.08)', Icon: Clock },
  running: { label: 'Running', color: '#3B82F6', bg: 'rgba(59,130,246,0.08)', Icon: Pulse },
  waiting: { label: 'Waiting', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', Icon: HourglassMedium },
  retrying: { label: 'Retrying', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', Icon: ArrowsClockwise },
  completed: { label: 'Completed', color: '#00E5C0', bg: 'rgba(0,229,192,0.06)', Icon: CheckCircle },
  failed: { label: 'Failed', color: '#EF4444', bg: 'rgba(239,68,68,0.06)', Icon: XCircle },
  cancelled: { label: 'Cancelled', color: '#7D8896', bg: 'rgba(107,118,133,0.06)', Icon: XCircle },
  partially_completed: { label: 'Partially done', color: '#F59E0B', bg: 'rgba(245,158,11,0.06)', Icon: Warning },
};

/* ── Timeline event type styling ── */
const EVENT_STYLE = {
  system_started: { Icon: Lightning, color: '#3B82F6' },
  run_started: { Icon: Play, color: '#3B82F6' },
  worker_started: { Icon: Pulse, color: '#3B82F6' },
  worker_completed: { Icon: CheckCircle, color: '#00E5C0' },
  worker_failed: { Icon: XCircle, color: '#EF4444' },
  run_completed: { Icon: CheckCircle, color: '#00E5C0' },
  run_failed: { Icon: XCircle, color: '#EF4444' },
  handoff: { Icon: ArrowRight, color: '#8B8FF5' },
  waiting: { Icon: HourglassMedium, color: '#F59E0B' },
  retry: { Icon: ArrowsClockwise, color: '#F59E0B' },
  retrying: { Icon: ArrowsClockwise, color: '#F59E0B' },
  resumed: { Icon: Play, color: '#3B82F6' },
  approved: { Icon: Stamp, color: '#00E5C0' },
  cancelled: { Icon: Stop, color: '#7D8896' },
  timeout: { Icon: Clock, color: '#EF4444' },
  step_skipped: { Icon: ArrowRight, color: '#5C6778' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

function formatDuration(ms) {
  if (!ms) return '';
  if (ms < 1000) return `${ms}ms`;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  return `${min}m ${remSec}s`;
}

function StatusBadge({ status, size = 'sm' }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.queued;
  const { Icon } = cfg;
  const sizeClasses = size === 'lg'
    ? 'px-3 py-1.5 text-xs gap-2'
    : 'px-2.5 py-1 text-[11px] gap-1.5';
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center rounded-full font-medium border ${sizeClasses}`}
      style={{ color: cfg.color, background: cfg.bg, borderColor: `${cfg.color}20` }}
    >
      <Icon size={size === 'lg' ? 14 : 12} weight="bold" />
      {cfg.label}
    </span>
  );
}

/* ── Worker step card ── */
function WorkerStep({ wr, index, total }) {
  const cfg = STATUS_CONFIG[wr.status] || STATUS_CONFIG.queued;
  const isLast = index === total - 1;

  return (
    <div className="flex gap-3" data-testid={`worker-step-${wr.worker_run_id}`}>
      {/* Vertical line connector */}
      <div className="flex flex-col items-center">
        <div
          className="w-3 h-3 rounded-full border-2 flex-shrink-0 mt-1"
          style={{ borderColor: cfg.color, background: wr.status === 'completed' ? cfg.color : 'transparent' }}
        />
        {!isLast && (
          <div className="w-px flex-1 mt-1" style={{ background: wr.status === 'completed' ? '#00E5C020' : '#1E2330' }} />
        )}
      </div>
      {/* Content */}
      <div className={`flex-1 pb-4 ${isLast ? '' : ''}`}>
        <div className="flex items-center justify-between">
          <span className="text-[13px] text-[#F0F4F8] font-medium">{wr.worker_name}</span>
          <StatusBadge status={wr.status} />
        </div>
        {wr.summary && (
          <p className="text-[11px] text-[#7D8896] mt-1">{wr.summary}</p>
        )}
        {wr.error_message && (
          <p className="text-[11px] text-[#EF4444] mt-1">{wr.error_message}</p>
        )}
        {wr.waiting_reason && (
          <p className="text-[11px] text-[#F59E0B] mt-1">
            {wr.waiting_reason === 'tool_connection' ? 'Waiting for tool connection' :
             wr.waiting_reason === 'approval_required' ? 'Waiting for approval' :
             `Waiting: ${wr.waiting_reason}`}
          </p>
        )}
        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#5C6778]">
          {wr.duration_ms > 0 && <span>{formatDuration(wr.duration_ms)}</span>}
          {wr.retry_count > 0 && <span>{wr.retry_count} retries</span>}
        </div>
      </div>
    </div>
  );
}

/* ── Timeline event ── */
function TimelineEvent({ event }) {
  const style = EVENT_STYLE[event.event_type] || { Icon: Lightning, color: '#5C6778' };
  const { Icon } = style;

  return (
    <div className="flex gap-3 py-2" data-testid={`timeline-event-${event.event_id}`}>
      <div className="flex flex-col items-center">
        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${style.color}12` }}>
          <Icon size={12} weight="bold" style={{ color: style.color }} />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-[#C8D1DB]">{event.description}</p>
        <p className="text-[10px] text-[#5C6778] mt-0.5">{timeAgo(event.created_at)}</p>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   MAIN DETAIL PAGE
   ════════════════════════════════════════════════ */
export default function SystemRunDetail() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;

  const [run, setRun] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!orgId || !runId) return;
    try {
      const [runRes, timelineRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/system-runs/${runId}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/system-runs/${runId}/timeline`, { credentials: 'include' }),
      ]);
      if (runRes.ok) {
        const data = await runRes.json();
        setRun(data);
      }
      if (timelineRes.ok) {
        const data = await timelineRes.json();
        setTimeline(data.events || []);
      }
    } catch (err) {
      console.error('Failed to load run detail:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, runId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // Auto-refresh for active runs
  useEffect(() => {
    if (!run) return;
    const isActive = ['running', 'waiting', 'queued', 'retrying'].includes(run.status);
    if (!isActive) return;
    const interval = setInterval(fetchDetail, 3000);
    return () => clearInterval(interval);
  }, [run, fetchDetail]);

  const handleAction = async (action) => {
    setActing(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/system-runs/${runId}/${action}`,
        { method: 'POST', credentials: 'include' }
      );
      if (res.ok) {
        const labels = { cancel: 'Cancelled', resume: 'Resumed', approve: 'Approved', retry: 'Retrying' };
        toast.success(labels[action] || 'Done');
        fetchDetail();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Action failed');
      }
    } catch {
      toast.error('Action failed');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[720px] mx-auto flex items-center justify-center py-20" data-testid="loading-state">
        <ArrowsClockwise size={20} className="text-[#3A4150] animate-spin" />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="max-w-[720px] mx-auto text-center py-20" data-testid="not-found-state">
        <p className="text-sm text-[#7D8896]">Run not found</p>
        <button
          onClick={() => navigate('/workers/activity')}
          className="text-xs text-[#3B82F6] hover:underline mt-2"
        >
          Back to activity
        </button>
      </div>
    );
  }

  const workerRuns = run.worker_runs || [];
  const chain = run.worker_chain || [];
  const isActive = ['running', 'waiting', 'queued', 'retrying'].includes(run.status);
  const canCancel = ['running', 'waiting', 'queued', 'retrying'].includes(run.status);
  const canResume = run.status === 'waiting';
  const canApprove = run.status === 'waiting';
  const canRetry = ['failed', 'partially_completed'].includes(run.status);

  return (
    <div className="max-w-[720px] mx-auto" data-testid="system-run-detail">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => navigate('/workers/activity')}
          data-testid="back-to-activity-btn"
          className="flex items-center gap-1 text-[#7D8896] hover:text-[#B0B8C4] text-xs transition-colors"
        >
          <ArrowLeft size={14} />
          Activity
        </button>
      </div>

      {/* Header */}
      <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.05)] rounded-xl p-6 mb-4" data-testid="run-header">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <p className="text-base font-semibold text-[#F0F4F8]" data-testid="run-summary">
              {run.supervisor_summary || 'System run'}
            </p>
            {run.error_summary && (
              <p className="text-[12px] text-[#EF4444] mt-1">{run.error_summary}</p>
            )}
          </div>
          <StatusBadge status={run.status} size="lg" />
        </div>
        {/* Meta row */}
        <div className="flex items-center gap-4 text-[11px] text-[#5C6778]">
          <span>{chain.length} worker{chain.length !== 1 ? 's' : ''}</span>
          {run.duration_ms && <span>{formatDuration(run.duration_ms)}</span>}
          <span>{timeAgo(run.created_at)}</span>
          {run.trigger_source && run.trigger_source !== 'manual' && (
            <span className="capitalize">via {run.trigger_source}</span>
          )}
        </div>

        {/* Actions */}
        {(canCancel || canResume || canApprove || canRetry) && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[rgba(255,255,255,0.04)]" data-testid="run-actions">
            {canApprove && (
              <button
                onClick={() => handleAction('approve')}
                disabled={acting}
                data-testid="approve-btn"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-[#00E5C0] text-[#12161C] hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
              >
                <Stamp size={14} />
                Approve
              </button>
            )}
            {canResume && !canApprove && (
              <button
                onClick={() => handleAction('resume')}
                disabled={acting}
                data-testid="resume-btn"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors disabled:opacity-50"
              >
                <Play size={14} />
                Resume
              </button>
            )}
            {canRetry && (
              <button
                onClick={() => handleAction('retry')}
                disabled={acting}
                data-testid="retry-btn"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-[rgba(245,158,11,0.3)] text-[#F59E0B] hover:bg-[rgba(245,158,11,0.06)] transition-colors disabled:opacity-50"
              >
                <ArrowClockwise size={14} />
                Retry
              </button>
            )}
            {canCancel && (
              <button
                onClick={() => handleAction('cancel')}
                disabled={acting}
                data-testid="cancel-btn"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-[rgba(239,68,68,0.2)] text-[#EF4444] hover:bg-[rgba(239,68,68,0.06)] transition-colors disabled:opacity-50"
              >
                <Stop size={14} />
                Cancel
              </button>
            )}
          </div>
        )}
      </div>

      {/* Worker progress */}
      {workerRuns.length > 0 && (
        <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.05)] rounded-xl p-5 mb-4" data-testid="worker-steps">
          <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-[0.08em] mb-4">Workers</p>
          {workerRuns.map((wr, i) => (
            <WorkerStep key={wr.worker_run_id} wr={wr} index={i} total={workerRuns.length} />
          ))}
        </div>
      )}

      {/* Timeline (collapsible) */}
      {timeline.length > 0 && (
        <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.05)] rounded-xl overflow-hidden" data-testid="timeline-section">
          <button
            onClick={() => setShowTimeline(!showTimeline)}
            data-testid="toggle-timeline-btn"
            className="w-full flex items-center justify-between p-5 text-left hover:bg-[rgba(255,255,255,0.01)] transition-colors"
          >
            <span className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-[0.08em]">
              Timeline ({timeline.length})
            </span>
            {showTimeline ? <CaretUp size={14} className="text-[#5C6778]" /> : <CaretDown size={14} className="text-[#5C6778]" />}
          </button>
          {showTimeline && (
            <div className="px-5 pb-5 border-t border-[rgba(255,255,255,0.03)]">
              <div className="pt-3 flex flex-col gap-0.5">
                {timeline.map(evt => (
                  <TimelineEvent key={evt.event_id} event={evt} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
